import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga";
import { useSelector } from "react-redux";
import { useSearchParams, Link } from "react-router-dom";
import Header from "../components/Frontend/Header";
import Footer from "../components/Frontend/Footer";
import ButtonComp from "components/PublicPagesReusable/ButtonComp";
import API from "api";
import { store } from "store/index";
import { analyze, LABELS } from "../util/pteScorecardEngine";
// import "../assets/css/scorecard-analyzer.css";
import ReactHtmlParser from "react-html-parser";
import { GoArrowUpLeft } from "react-icons/go";
import DatePicker from "react-datepicker";
import ScorecardAuthModal from "components/ScorecardAnalyzer/ScorecardAuthModal";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import varunImg from "components/ChatBot/varun.webp";
import { IoLockClosedOutline } from "react-icons/io5";

const DRAFT_KEY = "scorecardAnalyzerDraft";

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const saveDraft = (draft) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, savedAt: Date.now() }));
  } catch (err) {
    console.warn("[ScorecardAnalyzer] Could not persist draft", err);
  }
};

const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (err) {
    console.warn("[ScorecardAnalyzer] Could not clear draft", err);
  }
};

const contactFromUser = (user) => {
  const name =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.name ||
    user?.user_name ||
    "";
  return {
    name,
    email: user?.email || "",
    phone: user?.phone || "",
    user_id: user?.id != null ? String(user.id) : "",
  };
};

const blankGate = () => ({ name: "", email: "", phone: "", target: "", date: "", consent: false });

const TASK_ORDER = LABELS.taskOrder; // 22 question-type codes in display order
const TASK_NAME = LABELS.tasks; // { code: "Human task name" }
const MODULE_NAME = { L: "Listening", R: "Reading", S: "Speaking", W: "Writing" };

/* The 8 myPTE Skills-Profile bars (key + human label). */
const BARS = [
  { key: "ORSW", label: "Open response (speak+write)" },
  { key: "REPRO", label: "Reproducing language" },
  { key: "EW", label: "Extended writing" },
  { key: "SW", label: "Short writing" },
  { key: "ES", label: "Extended speaking" },
  { key: "SS", label: "Short speaking" },
  { key: "MSC", label: "Multi-skill comprehension" },
  { key: "SSC", label: "Single-skill comprehension" },
];

/* 9-step band scale the sliders map onto (same as prototype BANDS9/DISP9). */
const BANDS9 = ["VeryLow", "VL-Low", "Low", "Low-Mid", "Mid", "Mid-High", "High", "High-Full", "Full"];
const DISP9 = {
  VeryLow: "Very low",
  "VL-Low": "Very low – Low",
  Low: "Low",
  "Low-Mid": "Low – Mid",
  Mid: "Mid",
  "Mid-High": "Mid – High",
  High: "High",
  "High-Full": "High – Full",
  Full: "Full",
};

const REPS = {
  RA: "5–10/day", RS: "20–30/day", DI: "3–5/day", RL: "3–5/day", SGD: "3–5/day",
  RtS: "3–5/day", SWT: "2/day", WE: "1/day", SST: "2/day", WFD: "20–30/day",
  HIW: "5–10/day", RFIBd: "15–20/day", RFIBdd: "15–20/day", RO: "5–10/day",
  LFIB: "daily", RMCMA: "~5, last days", RMCSA: "~5, last days", LMCMA: "~5, last days",
  LMCSA: "~5, last days", SMW: "~5, last days", HCS: "~5, last days", ASQ: "~5, last days",
};

const CONF_LABEL = { high: "clear signal", med: "likely", low: "best estimate" };

/* Language Academy coaching tips — verbatim from the UI handoff
   (LA-Scorecard-Analyzer-UI-Handoff/coaching.js · LATIPS). Feeds the green
   "The Language Academy way" box on each focus card. */
const LATIPS = {
  RA: { tip: "Read aloud in a smooth flow at a moderate, clear pace, pausing slightly at commas and full stops.", technique: "Technique : one-line fallback — read any 8–12 consecutive words clearly and fluently, then move on" },
  RS: { tip: "Focus on the meaning, picture the sentence, then say back as much as you remember clearly and confidently, without stopping.", technique: "Technique : 70–80% of a long sentence spoken clearly still scores" },
  DI: { tip: "Describe the image in your own words with maximum relevant content: opener, the axes, two–three elements, the highest and lowest, a conclusion.", technique: "Technique : no memorised template (now penalised) — fall back on layout: left, right, top, bottom, middle, colours" },
  RL: { tip: "Take short keyword notes while you understand the lecture, then retell the main ideas in full, meaningful phrases for ~35 seconds.", technique: "Technique : minimum template, maximum content" },
  ASQ: { tip: "Answer the general-knowledge question in one to three words — never stay silent, never say \"I don't know\".", technique: "Technique : if unsure, repeat the keywords or the whole question" },
  SGD: { tip: "Lock onto the main topic, note ~three points per speaker, then deliver the relevant points fluently for ~90 seconds.", technique: "Technique : attribution to each speaker isn't checked — just cover the points" },
  RtS: { tip: "Understand the on-screen scenario, then respond appropriately and relevantly in about 35 seconds.", technique: "Technique : a light template is fine here — LA gives a ready one (greeting, topic, address it, polite close)" },
  SWT: { tip: "Write ONE single sentence of 5–75 words (ideally 60–65), joining 3–4 key sentences with commas and connectors.", technique: "Technique : combine key sentences with comma + \"furthermore/moreover\"; paraphrase a few words to synonyms" },
  WE: { tip: "Address the exact topic directly in 200–300 words of simple formal English, 4–5 paragraphs, then proofread.", technique: "Technique : minimise templates; invented plausible examples are fine; proofread backwards" },
  SST: { tip: "Capture topic, points and keywords as the audio plays, then write a 50–70 word summary (~65) in about five full sentences.", technique: "Technique : clear structure with connectors; proofread backwards" },
  WFD: { tip: "Write the first letter of each word as the audio plays, then expand the initials into the full sentence and type it.", technique: "Technique : initials method; if stuck, type a few common words (is/are/the) — extras aren't penalised" },
  HCS: { tip: "Note the main ideas during the audio, then pick the one option that covers all key ideas and nothing extra.", technique: "Technique : eliminate the three wrong types: extra-information, missing-the-point, false" },
  HIW: { tip: "Track the transcript word by word with the speaker and click each written word that differs from what you hear, in real time.", technique: "Technique : listen for similar-sounding near-matches; click again to deselect before the audio ends" },
  RFIBd: { tip: "Read the whole sentence, guess the word yourself first, then pick the matching dropdown and confirm with grammar.", technique: "Technique : predict-then-match; the 12 grammar rules built on verb forms and active/passive voice" },
  RFIBdd: { tip: "Predict each word first, drag in the ones you're sure of using grammar, and leave the extra distractor words behind.", technique: "Technique : same grammar skill as Dropdown; ~20 blanks a day for a week builds the pattern" },
  RO: { tip: "Find the independent opener with the \"stranger\" test, then build the order using the object→subject noun chain.", technique: "Technique : pronoun & transition elimination; the object→subject chain is the most reliable hack" },
  RMCMA: { tip: "Read the question first, match meaning not keywords, and only tick options you're genuinely confident about.", technique: "Technique : when unsure, select fewer; keep it quick (~30–40s)" },
  RMCSA: { tip: "Read the question first, locate the information, and pick the option that paraphrases the passage — always attempt it.", technique: "Technique : eliminate the two clearly-wrong options, then guess between the rest" },
  LFIB: { tip: "Track the transcript and jot the missing words in a vertical list as you listen, then type, check spelling/endings, and move on.", technique: "Technique : note in order, don't type during audio; check capitals and S/E endings; fill every blank" },
  LMCMA: { tip: "Read the options in the lead-in, choose by meaning while the audio plays, and select conservatively (negative marking).", technique: "Technique : select just ONE option (enough for 90); add a second only if 100% sure" },
  LMCSA: { tip: "Read the question and options in the lead-in, decide while the audio plays, and pick one — never leave it blank.", technique: "Technique : eliminate the clearly-wrong options and pick the best of what remains" },
  SMW: { tip: "Follow the overall meaning and pick the option that fits the sense of what you heard before the beep.", technique: "Technique : meaning first, then the topic, then a grammar check against the last few words" },
};

const TICKS = [
  "Reading your Score Report…",
  "Mapping 8 Skills-Profile bars…",
  "Cross-referencing 22 question types…",
  "Running leg-aware elimination…",
  "Isolating shared root causes…",
  "Ranking by points leverage…",
];

/* Landing FAQ — copy lifted verbatim from the UI handoff
   (Scorecard Analyzer.dc.html · FAQS). Visible answers, indexable. */
const FAQS = [
  [
    "What is the PTE Skills Profile and why does it matter?",
    "The Skills Profile is the set of eight bars at the bottom of your myPTE score report — Open response speaking & writing, Reproducing spoken & written language, Extended writing, Short writing, Extended speaking, Short speaking, Multiple-skill comprehension and Single-skill comprehension. The bars hold no numbers; they show relative strength. A low bar tells you which question types are dragging a communicative skill down — that is what the analyzer reads.",
  ],
  [
    "Why is my Listening score low when my Speaking is high?",
    "In PTE Academic most Listening marks come from integrated tasks — Summarize Group Discussion, Repeat Sentence, Retell Lecture, Write from Dictation and Summarize Spoken Text. If Speaking is strong but Listening trails it, the gap usually sits in the listening leg of those shared tasks, not in the quick single-skill questions. The analyzer compares the two scores and your comprehension bars to point at the right leg.",
  ],
  [
    "Does the analyzer predict my next PTE score?",
    "No. It is a relative diagnostic built on Pearson\u2019s official scoring model: it tells you where points are leaking and which question types carry the most weight for each skill. It never promises a number. For an exact plan, check it with a Language Academy coach.",
  ],
  [
    "What if I don\u2019t have my Skills Profile bars?",
    "Tick \"I don\u2019t have my bars\". The analyzer then works from your four scores alone and points you to the highest-impact question types for each weak skill. Add the bars later, or take a full scored Mock Test, for a sharper plan.",
  ],
  [
    "Which PTE question types move my score the most?",
    "Describe Image and Summarize Group Discussion for Speaking; Write Essay, Summarize Written Text and Write from Dictation for Writing; Fill in the Blanks (Dropdown and Drag & Drop) and Summarize Written Text for Reading; Summarize Group Discussion, Repeat Sentence and Retell Lecture for Listening. Multiple-choice and Select Missing Word carry very little weight — brush up on them only in the last few days before your test.",
  ],
  [
    "Is the Scorecard Analyzer free?",
    "Yes. Enter your scores and bars, and your ranked focus plan is free. Language Academy students also get it inside the Practice Portal under Tools, with results saved to their record.",
  ],
];

const clampScore = (v) => {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return "";
  return Math.max(10, Math.min(90, n));
};

const isoToLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const localDateToIso = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const blankEntry = () => ({
  scores: { L: "", R: "", S: "", W: "" },
  targets: { L: 90, R: 90, S: 90, W: 90 },
  bars: { ORSW: "Mid", REPRO: "Mid", EW: "Mid", SW: "Mid", ES: "Mid", SS: "Mid", MSC: "Mid", SSC: "Mid" },
});

const entryFromDraft = (draft) => ({
  ...blankEntry(),
  ...(draft?.entry || {}),
  scores: { ...blankEntry().scores, ...(draft?.entry?.scores || {}) },
  targets: { ...blankEntry().targets, ...(draft?.entry?.targets || {}) },
  bars: { ...blankEntry().bars, ...(draft?.entry?.bars || {}) },
});

/* Sync bootstrap so ?resumeReport / ?resumeUnlock never flash hero/entry first. */
const resolveResumeBootstrap = () => {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const resumeReport = params.get("resumeReport") === "1";
    const resumeUnlock = params.get("resumeUnlock") === "1";
    if (!resumeReport && !resumeUnlock) return null;

    const draft = loadDraft();
    if (!draft?.report) return null;

    if (resumeReport && (draft.reportReady || draft.gateVisible === false)) {
      return { mode: "unlocked", draft };
    }
    if (draft.pendingUnlock) {
      return { mode: "pending", draft };
    }
  } catch {
    /* ignore */
  }
  return null;
};

/* Build the API request body. The backend expects exactly the user's entered
   scores, targets and the 8 Skills-Profile bars (see /api/v2/scorecard-analyzer). */
const buildPayload = ({ scores, targets, bars }) => ({
  scores,
  targets,
  bars,
});

const ytLink = (name) =>
  "https://www.youtube.com/@LanguageAcademyPTE/search?query=" +
  encodeURIComponent((name || "").replace(/^[RL] · /, "").replace(/\s*\(.*\)/, ""));

const ScorecardAnalyzer = () => {
  const token = useSelector((state) => state.token);
  const user = useSelector((state) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const isLoggedIn = Boolean(token);

  const [boot] = useState(() => resolveResumeBootstrap());
  const bootDraft = boot?.draft || null;

  const [screen, setScreen] = useState(() => (boot ? "report" : "hero")); // hero | entry | analyzing | report
  const [entry, setEntry] = useState(() => (bootDraft ? entryFromDraft(bootDraft) : blankEntry()));
  const [report, setReport] = useState(() => bootDraft?.report || null);
  const [reportScores, setReportScores] = useState(() => bootDraft?.reportScores || null);
  const [scoreErrors, setScoreErrors] = useState({});
  const [ticker, setTicker] = useState(TICKS[0]);
  const [faqOpen, setFaqOpen] = useState(-1); // Landing FAQ accordion (UI only)
  const [helpOpen, setHelpOpen] = useState(false); // Entry "Where do I find these?" (UI only)
  const [openCards, setOpenCards] = useState({}); // report "See how we can tell" toggles (UI only)

  // gate (free mode) — full report stays locked until unlock succeeds
  const [gateVisible, setGateVisible] = useState(() => (boot?.mode === "unlocked" ? false : true));
  const [gate, setGate] = useState(() =>
    bootDraft?.gate ? { ...blankGate(), ...bootDraft.gate } : blankGate()
  );
  const [gateErrors, setGateErrors] = useState({});
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingUnlock, setPendingUnlock] = useState(() => boot?.mode === "pending");

  // API state
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [unlocking, setUnlocking] = useState(false); // gate report POST in flight
  const unlockingRef = useRef(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const [apiMessage, setApiMessage] = useState(""); // success message from the API

  // confirm modal (goal sanity check)
  const [modal, setModal] = useState(null); // { title, body, yes, no, onYes }

  const tickerTimer = useRef(null);
  const analyzeTimer = useRef(null);
  const draftHydratedRef = useRef(Boolean(boot));
  const autoUnlockRef = useRef(false);

  const latestRef = useRef({});
  latestRef.current = { entry, report, reportScores, gate, pendingUnlock, token, user };

  useEffect(() => {
    ReactGA.initialize("UA-157984080-1");
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    return () => {
      if (tickerTimer.current) clearInterval(tickerTimer.current);
      if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
    };
  }, []);

  /* Draft restore / clean URL. Unlocked resume is already applied on first paint. */
  useEffect(() => {
    if (boot?.mode === "unlocked") {
      clearDraft();
      if (searchParams.get("resumeReport") || searchParams.get("resumeUnlock")) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (boot?.mode === "pending") {
      if (searchParams.get("resumeReport") || searchParams.get("resumeUnlock")) {
        setSearchParams({}, { replace: true });
      }
      return;
    }

    if (draftHydratedRef.current) return;
    draftHydratedRef.current = true;

    // Refresh / other visits → clean start at the Hero (never stale Step 3).
    // "Analyse my scorecard" then moves the user to the entry screen.
    clearDraft();
    setPendingUnlock(false);
    setGateVisible(true);
    setReport(null);
    setReportScores(null);
    setEntry(blankEntry());
    setGate(blankGate());
    setScreen("hero");
    if (searchParams.get("resumeUnlock") || searchParams.get("resumeReport")) {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Persist only while register → thank-you → return is in flight */
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    if (!pendingUnlock) return;
    if (!report) return;

    saveDraft({
      screen: "report",
      entry,
      report,
      reportScores,
      gate,
      gateVisible: true,
      pendingUnlock: true,
    });
  }, [pendingUnlock, entry, report, reportScores, gate, gateVisible]);

  /* Prefill contact from account for logged-in users (name/email/phone locked) */
  useEffect(() => {
    if (!isLoggedIn || !user) return;
    const contact = contactFromUser(user);
    setGate((prev) => ({
      ...prev,
      name: contact.name || prev.name,
      email: contact.email || prev.email,
      phone: contact.phone || prev.phone,
    }));
  }, [isLoggedIn, user]);

  const go = useCallback((next) => {
    setScreen(next);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const resetEntryState = () => {
    setEntry(blankEntry());
    setReport(null);
    setReportScores(null);
    setScoreErrors({});
    const contact = isLoggedIn ? contactFromUser(user) : blankGate();
    setGate({
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      target: "",
      date: "",
    });
    setGateErrors({});
    setGateVisible(true);
    setAuthOpen(false);
    setPendingUnlock(false);
    setApiError("");
    setApiSuccess("");
    setApiMessage("");
    clearDraft();
  };

  /* ── "Analyse my scorecard" → navigate to the entry screen (no API call) ── */
  const startEntry = () => {
    resetEntryState();
    go("entry");
  };

  const confirmStartEntry = () => {
    setModal({
      title: "Start a new analysis?",
      body: "Your current report and the scores, goals and Skills Profile bars you entered will be reset. You’ll need to enter them again to get a new report. Your desired score and exam date will be kept.",
      yes: "Confirm",
      no: "Cancel",
      onYes: startEntry,
    });
  };

  const setScore = (k, value) => {
    setEntry((prev) => ({ ...prev, scores: { ...prev.scores, [k]: value === "" ? "" : +value } }));
    if (scoreErrors[k]) {
      setScoreErrors((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
    }
  };

  const blurScore = (k) => {
    setEntry((prev) => {
      const c = clampScore(prev.scores[k]);
      return { ...prev, scores: { ...prev.scores, [k]: c === "" ? "" : c } };
    });
  };

  const setTarget = (k, value) => {
    setEntry((prev) => ({ ...prev, targets: { ...prev.targets, [k]: value === "" ? "" : +value } }));
  };

  const blurTarget = (k) => {
    setEntry((prev) => {
      const c = clampScore(prev.targets[k]);
      return { ...prev, targets: { ...prev.targets, [k]: c || 90 } };
    });
  };

  const setBar = (key, idx) => {
    setEntry((prev) => ({ ...prev, bars: { ...prev.bars, [key]: BANDS9[idx] } }));
  };

  /* "Try a sample scorecard" — fills the entry form with the handoff sample
     values (Scorecard Analyzer.dc.html · SAMPLE). Pure UI prefill; no API. */
  const fillSample = () => {
    setEntry((prev) => ({
      ...prev,
      scores: { L: 62, R: 50, S: 84, W: 88 },
      bars: { ORSW: "Mid", REPRO: "Mid", EW: "High", SW: "High", ES: "High", SS: "High", MSC: "Low", SSC: "High" },
    }));
    setScoreErrors({});
  };

  /* ── "Analyse my results" flow ──────────────────────────────────────────── */
  const runAnalysis = () => {
    const sc = entry.scores || {};
    const bad = ["L", "R", "S", "W"].filter((k) => !(sc[k] >= 10 && sc[k] <= 90));
    if (bad.length) {
      const errs = {};
      bad.forEach((k) => (errs[k] = true));
      setScoreErrors(errs);
      setModal({
        title: "Add all four scores",
        body: "Please enter a score between 10 and 90 for Listening, Reading, Speaking and Writing before we analyse.",
        yes: "Got it",
        no: null,
        onYes: null,
      });
      return;
    }

    const tg = entry.targets || { L: 90, R: 90, S: 90, W: 90 };
    const allBelow = ["L", "R", "S", "W"].every((k) => (tg[k] || 90) < sc[k]);
    if (allBelow) {
      setModal({
        title: "Quick check on your goals",
        body: "You've set your goal below your current score in all four skills — most people aim at or above what they scored. If that's intentional, carry on; otherwise pop back and fix it.",
        yes: "Yes, that's right",
        no: "Let me fix it",
        onYes: proceedAnalysis,
      });
      return;
    }
    proceedAnalysis();
  };

  const proceedAnalysis = () => {
    setModal(null);
    setApiError("");
    setApiSuccess("");
    setGateVisible(true);
    // freeze the exact values the user entered for the POST
    const frozen = { scores: { ...entry.scores }, targets: { ...entry.targets }, bars: { ...entry.bars } };

    go("analyzing");
    let i = 0;
    setTicker(TICKS[0]);
    if (tickerTimer.current) clearInterval(tickerTimer.current);
    tickerTimer.current = setInterval(() => {
      i += 1;
      if (i < TICKS.length) setTicker(TICKS[i]);
    }, 520);

    // The report is rendered from the API response. Kick off the POST and keep
    // the "analysing" animation on screen for at least a short beat.
    submitAnalysis(frozen);
  };

  /* Render the report from an analysis object ({ scores, targets, mods, ... })
     — used for both the API response and the local-engine fallback. */
  const revealReport = (rv, frozen) => {
    if (tickerTimer.current) clearInterval(tickerTimer.current);
    setReport(rv);
    setReportScores({
      scores: rv?.scores || frozen.scores,
      targets: rv?.targets || rv?.goals || frozen.targets,
      bars: rv?.bars || frozen.bars,
    });
    go("report");
  };

  /* POST /scorecard-analyzer — send the entered scores/targets/bars, then
     render the report the API returns (falls back to the local engine). */
  const submitAnalysis = async (frozen) => {
    if (submittingRef.current) return;
    const payload = buildPayload({
      scores: frozen.scores,
      targets: frozen.targets,
      bars: frozen.bars,
    });

    submittingRef.current = true;
    setSubmitting(true);
    setApiError("");

    // Guarantee the "analysing" animation shows for at least ~1.6s.
    const minDelay = new Promise((resolve) => {
      if (analyzeTimer.current) clearTimeout(analyzeTimer.current);
      analyzeTimer.current = setTimeout(resolve, 1600);
    });

    try {
      const res = await API({ method: "POST", url: "scorecard-analyzer", data: payload });
      await minDelay;
      const data = res?.data?.data || res?.data;
      if (res && (res.status === 200 || res.status === 201) && data && data.mods) {
        setApiMessage(res?.data?.message || "");
        revealReport(data, frozen);
      } else {
        // Unexpected shape — fall back to the local engine so the user still gets a report.
        setApiError("We used an offline analysis — some details may differ from the server.");
        revealReport(analyze(frozen.scores, frozen.bars, frozen.targets), frozen);
      }
    } catch (err) {
      await minDelay;
      setApiError("We couldn't reach the analyzer, so this is an offline estimate.");
      revealReport(analyze(frozen.scores, frozen.bars, frozen.targets), frozen);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  /* ── Gate submit → unlock full report ───────────────────────────────────── */
  const validateGate = () => {
    const errs = {};
    // Logged-in: contact from account; only target + exam date are collected on the gate.
    // Guests collect target + exam date inside the register modal.
    if (isLoggedIn) {
      if (!gate.name.trim()) errs.name = "Your account is missing a name. Please update your profile.";
      if (!gate.email.trim()) errs.email = "Your account is missing an email. Please update your profile.";
      else if (!/^\S+@\S+\.\S+$/.test(gate.email.trim())) errs.email = "Please enter a valid email address.";
      if (!gate.phone.trim()) errs.phone = "Your account is missing a phone number. Please update your profile.";
      if (!String(gate.target).trim()) errs.target = "Please enter your target score.";
      else {
        const n = parseInt(gate.target, 10);
        if (Number.isNaN(n) || n < 10 || n > 90) errs.target = "Target score must be between 10 and 90.";
      }
      // if (!gate.date.trim()) errs.date = "Please enter your exam date.";
      if (!gate.consent) errs.consent = "Please agree to be contacted to unlock your report.";
    }
    setGateErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const setGateField = (field, value) => {
    // Logged-in users may only edit target score + exam date
    if (isLoggedIn && (field === "name" || field === "email" || field === "phone")) return;
    setGate((prev) => ({ ...prev, [field]: value }));
    if (gateErrors[field]) {
      setGateErrors((prev) => {
        const n = { ...prev };
        delete n[field];
        return n;
      });
    }
  };

  const persistUnlockDraft = (extra = {}) => {
    const snap = {
      screen: "report",
      entry: latestRef.current.entry,
      report: latestRef.current.report,
      reportScores: latestRef.current.reportScores,
      gate: latestRef.current.gate,
      gateVisible: true,
      pendingUnlock: true,
      ...extra,
    };
    saveDraft(snap);
    setPendingUnlock(true);
  };

  /* POST /scorecard-analyzer/report with JWT + draft scores, then unlock full report */
  const callReportApi = async (authUser) => {
    if (unlockingRef.current) return false;

    const jwt = store.getState().token || latestRef.current.token;
    if (!jwt) {
      setApiError("Please register or log in to unlock your full report.");
      return false;
    }

    const src =
      latestRef.current.reportScores || {
        scores: latestRef.current.entry.scores,
        targets: latestRef.current.entry.targets,
        bars: latestRef.current.entry.bars,
      };
    const g = latestRef.current.gate;
    const contact = contactFromUser(authUser || latestRef.current.user || {});
    const payload = {
      name: (contact.name || g.name || "").trim(),
      user_id: contact.user_id || "",
      email: (contact.email || g.email || "").trim(),
      phone: (contact.phone || g.phone || "").trim(),
      target_score: String(g.target).trim(),
      exam_date: String(g.date).trim() || null,
      scores: src.scores,
      targets: src.targets,
      bars: src.bars,
    };

    if (!payload.email || !payload.name) {
      setApiError("Your account is missing name or email. Please update your profile and try again.");
      return false;
    }

    unlockingRef.current = true;
    setUnlocking(true);
    setApiError("");
    setApiSuccess("");

    try {
      const res = await API({
        method: "POST",
        url: "scorecard-analyzer/report",
        data: payload,
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res && (res.status === 200 || res.status === 201)) {
        const data = res?.data?.data;
        if (data && data.mods) {
          setReport(data);
          setReportScores({
            scores: data.scores || src.scores,
            targets: data.targets || data.goals || src.targets,
            bars: data.bars || src.bars,
          });
        }
        setApiSuccess(res?.data?.message || "Your full report is unlocked below.");
        setGateVisible(false);
        setPendingUnlock(false);
        clearDraft();
        return true;
      }
      setApiError("Something went wrong. Please try again.");
      return false;
    } catch (err) {
      if (err?.response?.status === 422) {
        const se = err.response.data?.errors || err.response.data || {};
        const firstMsg = (arr) => (Array.isArray(arr) ? arr[0] : arr);
        const mapped = {};
        if (se.name) mapped.name = firstMsg(se.name);
        if (se.email) mapped.email = firstMsg(se.email);
        if (se.phone) mapped.phone = firstMsg(se.phone);
        if (se.target_score) mapped.target = firstMsg(se.target_score);
        if (se.exam_date) mapped.date = firstMsg(se.exam_date);
        if (Object.keys(mapped).length) {
          setGateErrors((prev) => ({ ...prev, ...mapped }));
          setApiError("Please fix the highlighted fields and try again.");
        } else {
          setApiError("Please check your details and try again.");
        }
      } else if (err?.response?.status === 401) {
        setApiError("Your session expired. Please register or log in again to unlock.");
        setPendingUnlock(true);
        setAuthOpen(true);
      } else {
        setApiError("We couldn't unlock your report right now. Please try again in a moment.");
      }
      return false;
    } finally {
      unlockingRef.current = false;
      setUnlocking(false);
    }
  };

  /* Unlock CTA:
     - Logged in → report API (prefilled contact; target/date on gate)
     - Guest → open register modal (details + OTP + target/date). On success
       call report API and open the full report on this page (no thank-you hop). */
  const unlock = async () => {
    if (unlockingRef.current) return;
    if (!report && !reportScores) {
      setApiError("Please analyse your scorecard before unlocking the full report.");
      return;
    }

    setApiError("");
    setApiSuccess("");

    if (isLoggedIn) {
      if (!validateGate()) return;
      await callReportApi(user);
      return;
    }

    // Guest: no gate fields — register modal collects everything needed
    persistUnlockDraft();
    setAuthOpen(true);
  };

  const handleAuthenticated = async (authUser, meta = {}) => {
    // Apply target/date collected in the register modal before calling report
    if (meta.target || meta.date) {
      setGate((prev) => ({
        ...prev,
        target: meta.target != null ? String(meta.target) : prev.target,
        date: meta.date != null ? String(meta.date) : prev.date,
      }));
      latestRef.current.gate = {
        ...latestRef.current.gate,
        target: meta.target != null ? String(meta.target) : latestRef.current.gate.target,
        date: meta.date != null ? String(meta.date) : latestRef.current.gate.date,
      };
    }

    const contact = contactFromUser(authUser);
    setGate((prev) => ({
      ...prev,
      name: contact.name || prev.name,
      email: contact.email || prev.email,
      phone: contact.phone || prev.phone,
    }));
    latestRef.current.gate = {
      ...latestRef.current.gate,
      name: contact.name || latestRef.current.gate.name,
      email: contact.email || latestRef.current.gate.email,
      phone: contact.phone || latestRef.current.gate.phone,
    };
    latestRef.current.user = authUser;
    latestRef.current.token = store.getState().token;

    // New signup → hard-redirect to thank-you (keeps modal up; no scorecard flash)
    if (meta.via === "signup") {
      persistUnlockDraft({
        gate: { ...latestRef.current.gate },
        pendingUnlock: true,
      });
      window.location.replace("/register-success?from=scorecard-analyzer");
      return;
    }

    setAuthOpen(false);

    // Login / Google: unlock report on this page immediately
    setPendingUnlock(true);
    const ok = await callReportApi(authUser);
    if (!ok) {
      persistUnlockDraft({
        gate: { ...latestRef.current.gate },
      });
    }
  };

  /* Resume unlock after register-success round-trip (legacy) or failed retry */
  useEffect(() => {
    if (!draftHydratedRef.current) return;
    if (autoUnlockRef.current) return;
    if (!token || !pendingUnlock || !gateVisible) return;
    if (!report && !reportScores) return;
    if (!gate.date || !String(gate.target).trim()) return;

    autoUnlockRef.current = true;
    callReportApi(user).finally(() => {
      autoUnlockRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pendingUnlock, gateVisible, report, reportScores, gate.date, gate.target]);

  /* ── Derived report data ────────────────────────────────────────────────── */
  const focusQ = new Set();
  if (report && report.mods) {
    Object.values(report.mods).forEach((d) => {
      if (d.status === "gap") (d.focus || []).forEach((t) => focusQ.add(t));
    });
  }
  const anyWeak = report && report.mods && Object.values(report.mods).some((d) => d.status === "gap");

  const skClass = (d) => {
    if (!d) return "";
    if (d.status === "gap") return "lowsk";
    if (d.status === "na" || d.status === "polish") return "midsk";
    return "histrong";
  };
  const skOverlay = (k, d) => {
    if (!d) return "";
    const sc = (reportScores && reportScores.scores) || {};
    if (d.status === "gap") return `${d.desired - sc[k]} below goal`;
    if (d.status === "polish") return "almost at goal";
    if (d.status === "met") return `goal of ${d.desired} met`;
    if (d.status === "max") return "maxed at 90";
    return "not readable";
  };

  const priorityList = () => {
    if (!report || !report.mods) return [];
    const items = [];
    ["S", "W", "L", "R"].forEach((k) => {
      const d = report.mods[k];
      if (!d || d.status !== "gap" || !d.focus || !d.focus.length) return;
      d.focus.slice(0, 3).forEach((t, i) => {
        const also = ["S", "W", "L", "R"].filter((x) => x !== k);
        items.push({ t, k, sc: d.score, def: d.desired - d.score, prim: i === 0, conf: d.conf, also: also });
      });
    });
    const seen = new Map();
    items.forEach((it) => {
      const e = seen.get(it.t);
      if (!e || it.def > e.def) seen.set(it.t, it);
    });
    return [...seen.values()].sort((a, b) => b.def - a.def || (a.prim ? 0 : 1) - (b.prim ? 0 : 1)).slice(0, 6);
  };

  const pri = priorityList();
  const orderedMods =
    report && report.mods
      ? ["S", "W", "L", "R"]
          .map((k) => report.mods[k])
          .sort((a, b) => (a.status === "gap" ? 0 : 1) - (b.status === "gap" ? 0 : 1) || a.score - b.score)
      : [];

  const sc = (reportScores && reportScores.scores) || {};

  /* Low-value tasks to brush up only in the last few days (aggregated across
     the report's skills, deduped) — feeds the plan footer line. */
  const brushTasks = report && report.mods
    ? [...new Set(
        Object.values(report.mods)
          .flatMap((d) => (d && d.brush) || [])
      )]
    : [];

  return (
    <div className="new-design">
      <Helmet>
        <title>PTE Scorecard Analyzer — Language Academy</title>
        <meta
          name="description"
          content="Enter your four PTE scores and your myPTE Skills-Profile bars to get a personalised, AI-assisted breakdown of exactly which tasks to fix — built on Pearson's official scoring model."
        />
      </Helmet>

      <Header />

      <div className="la-analyzer">
        <div className="amb">
          <div className="grid-bg" />
          <div className="orb a" />
          <div className="orb b" />
        </div>

        <main>
          {/* HERO */}
          <section className={`screen ${screen === "hero" ? "active" : ""}`}>
            <div className="wrap hero">
              <div className="sca-hero-grid">
                {/* Left column — headline, copy, CTA, trust */}
                <div className="sca-hero-left">
                  <span className="tag">
                    <span className="dot" /> Free · AI PTE Scorecard Analyzer
                  </span>
                  <h1 className="FS56FW500 top-section-text">
                    Stop guessing. See{" "}
                    <span className="text-success FS56FW500 fw-600">exactly</span> what&apos;s holding your score
                    back.
                  </h1>
                  <p className="plus-sans FS20FW500 sca-hero-sub">
                    {ReactHtmlParser("Enter your four PTE scores and your Skills-Profile bars from myPTE. In 60 seconds, get a personalised breakdown of where you're strong, where your points are really leaking, and the exact tasks to fix — built on Pearson's official scoring model.")}
                  </p>
                  <div className="cta sca-hero-cta">
                    <ButtonComp btnText="Analyse my scorecard" link={startEntry} />
                    <span className="sca-hero-note">No login · 60 seconds · no score prediction</span>
                  </div>
                  <div className="trust">
                    <div className="it">
                      <div className="n">14×</div>
                      <div className="l">Perfect 90 — Varun</div>
                    </div>
                    <div className="it">
                      <div className="n">50,000+</div>
                      <div className="l">Successful students</div>
                    </div>
                    <div className="it">
                      <div className="n">4.8/5</div>
                      <div className="l">App rating · 2,624 ratings</div>
                    </div>
                    <div className="it">
                      <div className="n">22</div>
                      <div className="l">Question types decoded</div>
                    </div>
                  </div>
                </div>

                {/* Right column — sample report card */}
                <div className="sca-hero-right">
                  <div className="sca-hero-cardshell">
                    <div className="sca-hero-card">
                      <div className="sca-hero-card-eyebrow">Sample report</div>
                      <div className="sca-hero-tiles">
                        <div className="sca-hero-tile danger">
                          <div className="sca-hero-tile-v">62</div>
                          <div className="sca-hero-tile-k">Listening</div>
                        </div>
                        <div className="sca-hero-tile danger">
                          <div className="sca-hero-tile-v">50</div>
                          <div className="sca-hero-tile-k">Reading</div>
                        </div>
                        <div className="sca-hero-tile warning">
                          <div className="sca-hero-tile-v">84</div>
                          <div className="sca-hero-tile-k">Speaking</div>
                        </div>
                        <div className="sca-hero-tile success">
                          <div className="sca-hero-tile-v">88</div>
                          <div className="sca-hero-tile-k">Writing</div>
                        </div>
                      </div>
                      <div className="sca-hero-card-lab">Where your points are leaking</div>
                      <div className="sca-hero-leaks">
                        <div className="sca-hero-leak">
                          <span className="sca-hero-leak-n first">1</span>
                          <span className="sca-hero-leak-nm">Fill Blanks (Dropdown)</span>
                          <span className="sca-hero-leak-mod">Reading</span>
                        </div>
                        <div className="sca-hero-leak">
                          <span className="sca-hero-leak-n">2</span>
                          <span className="sca-hero-leak-nm">Summarize Group Discussion</span>
                          <span className="sca-hero-leak-mod">Listening</span>
                        </div>
                        <div className="sca-hero-leak">
                          <span className="sca-hero-leak-n">3</span>
                          <span className="sca-hero-leak-nm">Repeat Sentence</span>
                          <span className="sca-hero-leak-mod">Listening</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* AUTHORITY STRIP */}
            <div className="sca-strip">
              <div className="sca-strip-inner">
                <span className="sca-strip-av">
                  <img src={varunImg} alt="Varun Dhawan" />
                </span>
                <span className="sca-strip-txt">
                  Coached the <b>Language Academy</b> way · Varun Dhawan · 14× Perfect 90
                </span>
                <span className="sca-strip-note">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#80AD2A" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Built on Pearson&apos;s official scoring model
                </span>
              </div>
            </div>

            {/* HOW IT WORKS */}
            <section className="sca-how">
              <div className="sca-eyebrow">How it works</div>
              <h2 className="sca-h2">Three Steps. One Clear Plan.</h2>
              <div className="sca-how-grid">
                <div className="sca-how-card">
                  <div className="sca-how-n">01</div>
                  <div className="sca-how-t">Enter your four scores</div>
                  <div className="sca-how-d">
                    Listening, Reading, Speaking, Writing — and the goal you&apos;re chasing in each. Default goal
                    is 90.
                  </div>
                </div>
                <div className="sca-how-card">
                  <div className="sca-how-n">02</div>
                  <div className="sca-how-t">Tap in your Skills Profile</div>
                  <div className="sca-how-d">
                    The eight bars from your myPTE score report. Drag each slider to match the bar — very low to
                    full. Skip them if you don&apos;t have them.
                  </div>
                </div>
                <div className="sca-how-card">
                  <div className="sca-how-n">03</div>
                  <div className="sca-how-t">Get your focus plan</div>
                  <div className="sca-how-d">
                    We check all 22 question types and hand you the handful that actually move your score — in
                    priority order, with a daily plan.
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="sca-faq">
              <div className="sca-faq-inner">
                <div className="sca-faq-intro">
                  <div className="sca-eyebrow">Questions students ask</div>
                  <h2 className="sca-h2">How To Read Your PTE Score Report</h2>
                  <p className="sca-faq-lead">
                    Straight answers, written the Language Academy way. The analyzer applies exactly these rules to
                    your own numbers.
                  </p>
                  <div className="cta sca-faq-cta">
                    <ButtonComp btnText="Analyse my scorecard" link={startEntry} />
                  </div>
                </div>
                <div className="sca-faq-list">
                  {FAQS.map(([q, a], i) => {
                    const open = faqOpen === i;
                    return (
                      <div className={`sca-faq-item ${open ? "open" : ""}`} key={q}>
                        <button
                          type="button"
                          className="sca-faq-q"
                          aria-expanded={open}
                          onClick={() => setFaqOpen((prev) => (prev === i ? -1 : i))}
                        >
                          <span>{q}</span>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="sca-faq-chev">
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </button>
                        {open && <div className="sca-faq-a">{a}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </section>

          {/* ENTRY */}
          <section className={`screen ${screen === "entry" ? "active" : ""}`}>
            <div className="wrap panel pt-5">
              <span className="step-tag">Step 1 of 2 · Your results</span>
              <h2 style={{ marginTop: 10 }}>Enter Your PTE Results</h2>
              <p className="lead">
                Pop in your four scores, the goal you're chasing in each, and your eight Skills-Profile bars from
                myPTE. Takes about a minute — and the more accurate it is, the sharper your plan. Scores run from 10
                to 90.
              </p>

              <div className="sca-entry-actions">
                <button type="button" className="sca-pill" onClick={() => setHelpOpen((v) => !v)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <path d="M12 17h.01" />
                  </svg>
                  Where do I find these?
                </button>
                <button type="button" className="sca-pill sca-pill-muted" onClick={fillSample}>
                  Try a sample scorecard
                </button>
              </div>

              {helpOpen && (
                <div className="sca-help">
                  <b>In myPTE:</b> open <b>View Score Report</b>. The four <b>Communicative Skills</b> scores sit at
                  the top. Scroll to <b>Skills Profile</b> — eight horizontal bars. Read each bar's fill: empty-ish
                  is <b>Very low</b>, about half is <b>Mid</b>, nearly full is <b>Full</b>. The bars hold no numbers
                  — that's expected.
                </div>
              )}

              <div className="confirm-grid">
                <div className="cbox">
                  <h4>
                    <span className="sca-cbox-icon green">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3v18h18" />
                        <path d="m7 15 4-4 4 4 5-6" />
                      </svg>
                    </span>
                    Your scores &amp; goals
                  </h4>
                  <div className="scorehd">
                    <span />
                    <span>You scored</span>
                    <span>Your goal</span>
                  </div>
                  {["S", "W", "R", "L" ].map((k) => (
                    <div className="scorerow2" key={k}>
                      <label>{MODULE_NAME[k]}</label>
                      <input
                        type="text"
                        min="10"
                        max="90"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        placeholder="—"
                        className={scoreErrors[k] ? "invalid" : ""}
                        value={entry.scores[k]}
                        onChange={(e) => setScore(k, e.target.value)}
                        onBlur={() => blurScore(k)}
                      />
                      <input
                        type="text"
                        inputmode="numeric"
                        min="10"
                        max="90"
                        className="goalin"
                        value={entry.targets[k]}
                        onChange={(e) => setTarget(k, e.target.value)}
                        onBlur={() => blurTarget(k)}
                      />
                    </div>
                  ))}
                  <p className="lead">
                    The PTE perfect score is 90. Most students aim at or above what they scored.
                  </p>
                </div>
                <div className="cbox">
                  <h4>
                    <span className="sca-cbox-icon violet">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 6H3" />
                        <path d="M15 12H3" />
                        <path d="M17 18H3" />
                      </svg>
                    </span>
                    Skills Profile bars
                  </h4>
                  <p className="lead">
                    Drag each bar to match how full it looks on your myPTE report. There are no numbers on these — relative is fine.
                  </p>
                  {BARS.map((b) => {
                    let idx = BANDS9.indexOf(entry.bars[b.key] || "Mid");
                    if (idx < 0) idx = 4;
                    const frac = idx / 8;
                    const pct = `${frac * 100}%`;
                    return (
                      <div className="barrow" key={b.key}>
                        <div className="bl">
                          <span className="bn">{b.label}</span>
                          <span className="bv">{DISP9[BANDS9[idx]]}</span>
                        </div>
                        <div className="sca-bar" style={{ "--sca-bar-frac": frac }}>
                          <div className="sca-bar-track">
                            <div className="sca-bar-fill" style={{ width: pct }} />
                          </div>
                          <div className="sca-bar-thumb" />
                          <input
                            type="range"
                            min="0"
                            max="8"
                            step="1"
                            value={idx}
                            onChange={(e) => setBar(b.key, +e.target.value)}
                            aria-label={b.label}
                            aria-valuetext={DISP9[BANDS9[idx]]}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, marginTop: 26, justifyContent: "flex-end", alignItems: "center" }}>
                <button type="button" className="btn btn-ghost" onClick={() => go("hero")}>
                  <GoArrowUpLeft /> Back
                </button>
                <ButtonComp btnText="Analyse my results" link={runAnalysis} />
              </div>
            </div>
          </section>

          {/* ANALYZING */}
          <section className={`screen ${screen === "analyzing" ? "active" : ""}`}>
            <div className="wrap analyzing">
              <div className="ring" />
              <h2>Analysing your profile…</h2>
              <div className="ticker">{ticker}</div>
            </div>
          </section>

          {/* REVEAL + REPORT */}
          <section className={`screen ${screen === "report" ? "active" : ""}`}>
            <div className="wrap h-auto pt-5">
              <div className="reveal-head">
                <span className="step-tag">Step 2 of 2 · Your diagnosis</span>
                <h2 style={{ marginTop: 10 }}>
                  We checked all 22 question types.
                  <br />
                  {anyWeak
                    ? `Here ${focusQ.size === 1 ? "is your 1 focus area" : `are your ${focusQ.size} focus areas`}.`
                    : "You've met your goal in every skill."}
                </h2>
                <p>
                  {/* {anyWeak
                    ? "Strong tasks fade out. Your real weak spots pulse. Watch."
                    : "Here's your optional path to a perfect 90."} */}
                    The marked tasks are where your points are leaking; the rest fade back.
                </p>
              </div>

              {apiError && (
                <div className="statusbar err" style={{ marginTop: 22 }}>
                  {apiError}
                </div>
              )}

              {screen === "report" && (
                <div className="taskgrid">
                  {TASK_ORDER.map((q) => {
                    const weak = focusQ.has(q);
                    return (
                      <div className={`tchip in ${weak ? "weak" : "strong"} d-flex justify-content-center align-items-center`} key={q}>
                        {TASK_NAME[q]}
                        {/* <span className="tcs">{weak ? "focus" : ""}</span> */}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Free gate — highlights stay free; full report unlocks after report API */}
            {gateVisible && (
              <>
                <div className="wrap gatecap">
                  <div className="card capcard w-100">
                    <h3
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          width: "60px",
                          height: "60px",
                          flex: "0 0 60px",
                          borderRadius: "12px",
                          background: "#EAFFC8",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#5C7A1E",
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                        </svg>
                      </span>
                        <div className="d-flex flex-column">
                        <span>Your full plan is ready</span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#666",
                          }}
                        >Tell us where to send it and we'll unlock it right here.
                        </span>
                        </div>
                    </h3>
                    {/* <p>
                      {isLoggedIn
                        ? "Confirm your target and exam date to unlock your ranked focus plan."
                        : "Register for free to unlock your ranked focus plan, reasons, and practice videos."}
                    </p> */}
                    {apiError && <div className="statusbar err" style={{ marginTop: 16 }}>{apiError}</div>}
                    <div className="capform">
                      {isLoggedIn ? (
                        <>
                          <input
                            placeholder="Full name"
                            className={gateErrors.name ? "invalid" : ""}
                            value={gate.name}
                            readOnly
                            disabled
                          />
                          {gateErrors.name && <span className="cap-err">{gateErrors.name}</span>}
                          <input
                            placeholder="Email address"
                            className={gateErrors.email ? "invalid" : ""}
                            value={gate.email}
                            readOnly
                            disabled
                          />
                          {gateErrors.email && <span className="cap-err">{gateErrors.email}</span>}
                          <input
                            placeholder="Phone"
                            className={gateErrors.phone ? "invalid" : ""}
                            value={gate.phone}
                            readOnly
                            disabled
                          />
                          {gateErrors.phone && <span className="cap-err">{gateErrors.phone}</span>}
                          <div className="caprow">
                            <input
                              type="number"
                              min="10"
                              max="90"
                              placeholder="Target score"
                              className={gateErrors.target ? "invalid" : ""}
                              value={gate.target}
                              onChange={(e) => setGateField("target", e.target.value)}
                            />
                            <div className={`datefield ${gateErrors.date ? "invalid" : ""}`}>
                              <DatePicker
                                selected={isoToLocalDate(gate.date)}
                                onChange={(date) => setGateField("date", localDateToIso(date))}
                                dateFormat="dd/MM/yyyy"
                                placeholderText="Exam date (DD/MM/YYYY)"
                                minDate={new Date()}
                                showIcon
                                icon={<span className="date-cal-icon" aria-hidden="true">📅</span>}
                                wrapperClassName="datefield-wrap"
                                className={gateErrors.date ? "invalid" : ""}
                              />
                            </div>
                          </div>
                          {gateErrors.target && <span className="cap-err">{gateErrors.target}</span>}
                          {/* {gateErrors.date && <span className="cap-err">{gateErrors.date}</span>} */}
                          <label className="sca-consent">
                            <input
                              type="checkbox"
                              checked={gate.consent}
                              onChange={(e) => setGateField("consent", e.target.checked)}
                            />
                            <span className={gateErrors.consent ? "sca-consent-err" : ""}>
                              I agree to Language Academy contacting me about my results and courses.{" "}
                              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">
                                Privacy Policy
                              </Link>
                            </span>
                          </label>
                          {gateErrors.consent && <span className="cap-err">{gateErrors.consent}</span>}
                        </>
                      ) : (
                        <p className="sca-guest-hint">
                          Next you&apos;ll register in a popup (details, target score, exam date + OTP). We unlock your
                          full report on this page — no need to re-enter your scores.
                        </p>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ justifyContent: "center", marginTop: 4 }}
                        onClick={unlock}
                        disabled={unlocking}
                        aria-busy={unlocking}
                      >
                        {unlocking
                          ? "Unlocking…"
                          : isLoggedIn
                          ? "Unlock my full report"
                          : "Register & unlock my full report"}
                          <img alt="LA" src="/images/web-new-images/right-arrow.svg" />
                      </button>
                      <p style={{ fontSize: 14, marginTop: 2 }} className="align-items-center d-flex gap-1 justify-content-center">
                        <IoLockClosedOutline /> Private &amp; secure. We never share your scorecard.
                      </p>
                    </div>
                  </div>

                </div>
                  <div className="sca-teaser" aria-hidden="true">
                    {/* <div className="sca-teaser-tag">Preview · unlock to see your real numbers</div> */}
                    <div className="sca-teaser-card">
                      <div className="sca-teaser-hd">
                        <div>
                          <div className="sca-teaser-hd-t">Your PTE Scorecard Diagnosis</div>
                          <div className="sca-teaser-hd-s">
                            Score-vs-Skills-Profile reconciliation · a relative diagnostic, not a score prediction
                          </div>
                        </div>
                        <div className="sca-teaser-hd-actions">
                          <span className="sca-teaser-emailbtn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="2" />
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                            </svg>
                            Email Me This Report
                          </span>
                          <span className="sca-teaser-badge">Your report</span>
                        </div>
                      </div>

                      <div className="sca-teaser-body">
                        {/* 1 — scores vs goals */}
                        <div className="sca-teaser-sec">
                          <div className="sca-teaser-sec-lab">
                            <span className="sca-teaser-n">1</span> Your Scores vs Your Goals
                          </div>
                          <div className="sca-teaser-tiles">
                            {[
                              { v: 62, g: 90, k: "Listening", tone: "danger", lab: "23 below goal" },
                              { v: 50, g: 90, k: "Reading", tone: "danger", lab: "30 below goal" },
                              { v: 84, g: 90, k: "Speaking", tone: "warning", lab: "almost at goal" },
                              { v: 88, g: 90, k: "Writing", tone: "success", lab: "goal of 90 met" },
                            ].map((t) => (
                              <div className={`sca-teaser-tile ${t.tone}`} key={t.k}>
                                <div className="sca-teaser-tile-v">
                                  {t.v}
                                  <span className="sca-teaser-tile-goal">/ goal {t.g}</span>
                                </div>
                                <div className="sca-teaser-tile-k">{t.k}</div>
                                <div className="sca-teaser-tile-lab">{t.lab}</div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* 2 — where points are leaking */}
                        <div className="sca-teaser-sec">
                          <div className="sca-teaser-sec-lab">
                            <span className="sca-teaser-n">2</span> Where Your Points Are Leaking
                          </div>
                          <p className="sca-teaser-p">
                            The handful of tasks that actually move your score — in priority order, not a list of
                            everything.
                          </p>
                          <div className="sca-teaser-prio">
                            {[
                              { n: 1, nm: "Fill Blanks (Dropdown)", sub: "Reading · scored 50 · start here", conf: "clear signal", first: true },
                              { n: 2, nm: "Summarize Group Discussion", sub: "Listening · scored 62", conf: "likely" },
                              { n: 3, nm: "Repeat Sentence", sub: "Listening · scored 62", conf: "best estimate" },
                            ].map((p) => (
                              <div className="sca-teaser-prio-row" key={p.n}>
                                <span className={`sca-teaser-prio-n ${p.first ? "first" : ""}`}>{p.n}</span>
                                <div className="sca-teaser-prio-main">
                                  <span className="sca-teaser-prio-nm">{p.nm}</span>
                                  <span className="sca-teaser-prio-sub">{p.sub}</span>
                                </div>
                                <span className="sca-teaser-conf">{p.conf}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* 3 — skill by skill (single sample card) */}
                        <div className="sca-teaser-sec">
                          <div className="sca-teaser-sec-lab">
                            <span className="sca-teaser-n">3</span> What To Practise — Skill By Skill
                          </div>
                          <div className="sca-teaser-fcard">
                            <div className="sca-teaser-fcard-top">
                              <div className="sca-teaser-fcard-nm">Reading — close the dropdown gap</div>
                              <span className="sca-teaser-lev">Focus area</span>
                            </div>
                            <div className="sca-teaser-focusbox">
                              <div className="sca-teaser-focus-h">Practise these question types</div>
                              <div className="sca-teaser-focus-nm">Fill in the Blanks (Dropdown) + Summarize Written Text</div>
                            </div>
                            <p className="sca-teaser-why">
                              your Reading score trails your goal the most, and your comprehension bars
                              point straight at the dropdown-style tasks.
                            </p>
                            <div className="sca-teaser-tip">
                              <span className="sca-teaser-tip-av" />
                              <div>
                                <div className="sca-teaser-tip-h">The Language Academy way · Fill Blanks</div>
                                <div className="sca-teaser-tip-q">Read the whole sentence first, then let grammar pick the blank.</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 4 — daily plan */}
                        <div className="sca-teaser-sec">
                          <div className="sca-teaser-sec-lab">
                            <span className="sca-teaser-n">4</span> Your Daily Study Plan
                          </div>
                          <div className="sca-teaser-plan">
                            <div className="sca-teaser-plan-h">Every day · in priority order</div>
                            {[
                              ["Fill in the Blanks (Dropdown)", "15–20/day"],
                              ["Summarize Group Discussion", "3–5/day"],
                              ["Repeat Sentence", "20–30/day"],
                            ].map(([nm, reps]) => (
                              <div className="sca-teaser-plan-row" key={nm}>
                                <span>{nm}</span>
                                <span className="sca-teaser-plan-rep">{reps}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* 5 — bottom line */}
                        <div className="sca-teaser-sec">
                          <div className="sca-teaser-sec-lab">
                            <span className="sca-teaser-n">5</span> The Bottom Line
                          </div>
                          <div className="sca-teaser-bottom">
                            Your fastest points sit in Reading and the shared listening tasks. Fix those question
                            types first and the rest of your score follows.
                          </div>
                        </div>
                      </div>

                      <div className="sca-teaser-cta">
                        <div>
                          <div className="sca-teaser-cta-t">Want a coach to turn this into a 2-week plan?</div>
                          <div className="sca-teaser-cta-s">Free trial class · we'll bring this exact diagnosis.</div>
                        </div>
                        <span className="sca-teaser-cta-btn">Book my free trial</span>
                      </div>
                    </div>
                  </div>
                </>
              )}

            {/* Report */}
            {!gateVisible && report && (
              <div className="wrap h-100">
                <div className="report">
                  <div className="rep-hd">
                    <div>
                      <div className="t">Your PTE Scorecard Diagnosis</div>
                      <div className="s">
                        Score-vs-Skills-Profile reconciliation · relative diagnostic, not a score prediction
                      </div>
                    </div>
                    <div>

                    </div>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                    {/* <div className="badge cursor-pointer">📧 Email me this report</div> */}
                    <div className="badge">Free report</div>
                    </div>
                  </div>

                  <div className="rep-body">
                    {/* {(apiSuccess || apiMessage) && (
                      <div className="statusbar ok">{apiSuccess || apiMessage}</div>
                    )} */}

                    {/* Snapshot */}
                    <div className="sec">
                      <div className="sec-lab">
                        <span className="nm">1</span> Your scores vs your goals
                      </div>
                      <div className="snap">
                        {["L", "R", "S", "W"].map((k) => {
                          const d = report.mods[k];
                          return (
                            <div className={`sk ${skClass(d)}`} key={k}>
                              <div className="v">
                                {sc[k]}
                                {d && d.desired != null && <span className="goal">/ goal {d.desired}</span>}
                              </div>
                              <div className="k">{MODULE_NAME[k]}</div>
                              <div className="ov">{skOverlay(k, d)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Where your points are leaking */}
                    {pri.length > 0 && (
                      <div className="sec">
                        <div className="sec-lab">
                          <span className="nm">2</span> Where your points are leaking
                        </div>
                        <p style={{ fontSize: "13.5px", color: "var(--pmut)", margin: "-2px 0 13px" }}>
                          The handful of tasks that actually move your score — in priority order, not a list of
                          everything.
                        </p>
                        <div className="prio">
                          {pri.map((p, i) => (
                            <div className="prio-row" key={p.t}>
                              <span className="prio-n">{i + 1}</span>
                              <div className="prio-main">
                                <span className="nmt">{TASK_NAME[p.t]}</span>
                                <span className="prio-sub">
                                  {MODULE_NAME[p.k]} · scored {p.sc}
                                  {i === 0 ? " · start here" : ""}
                                </span>
                              </div>
                              <span className={`conf c-${p.conf}`}>{CONF_LABEL[p.conf]}</span>
                            </div>
                          ))}
                        </div>
                        <p className="prio-note">Number 1 moves your score the most — give it the most time.</p>
                      </div>
                    )}

                    {/* Skill by skill */}
                    <div className="sec">
                      <div className="sec-lab">
                        <span className="nm">3</span> What to practise — skill by skill
                      </div>
                      {orderedMods.map((d) => {
                        const gap = d.status === "gap";
                        const hard = d.tone === "hard";
                        const focusArr = d.focus || [];
                        const lead = focusArr[0] || null;
                        const badgeText = gap
                          ? hard
                            ? "Focus area"
                            : "Minor gap"
                          : d.status === "polish"
                          ? "Almost there"
                          : d.status === "met"
                          ? "Goal met"
                          : d.status === "max"
                          ? "Top of the scale"
                          : "Needs a mock";
                        const badgeClass = gap
                          ? hard
                            ? "high"
                            : "med"
                          : d.status === "polish"
                          ? "goalsoft"
                          : d.status === "max" || d.status === "met"
                          ? "goal"
                          : "na";
                        const leadTip = gap && lead ? LATIPS[lead] : null;
                        const hasMore = gap && (d.evidence || (d.second && d.second.length) || d.budget || (d.brush && d.brush.length));
                        const cardOpen = !!openCards[d.m];
                        return (
                          <div className={`fcard ${gap && hard ? "starthere" : ""}`} key={d.m}>
                            <div className="top">
                              <div className="nm">{d.headline}</div>
                              <div className="badges">
                                <span className={`lev ${badgeClass}`}>{badgeText}</span>
                              </div>
                            </div>
                            {gap && focusArr.length > 0 && (
                              <div className="doaction">
                                <span className="do-h">Practise these question types</span>
                                {focusArr.map((t) => TASK_NAME[t]).join("  +  ")}
                              </div>
                            )}
                            <div
                              className="why"
                              // dangerouslySetInnerHTML={{ __html: `${gap ? "<b>Why:</b> " : ""}${d.detail || ""}` }}
                              dangerouslySetInnerHTML={{ __html: `${d.detail || ""}` }}
                            />
                            {leadTip && (
                              <div className="latip">
                                <span className="latip-av">
                                  <img src={varunImg} alt="Varun Dhawan" />
                                </span>
                                <div className="latip-body">
                                  <div className="latip-h">The Language Academy way · {TASK_NAME[lead]}</div>
                                  <div className="latip-q">{leadTip.tip}</div>
                                  {leadTip.technique && <div className="latip-more">{leadTip.technique}</div>}
                                </div>
                              </div>
                            )}
                            {hasMore && (
                              <button
                                type="button"
                                className="seehow"
                                aria-expanded={cardOpen}
                                onClick={() =>
                                  setOpenCards((prev) => ({ ...prev, [d.m]: !prev[d.m] }))
                                }
                              >
                                {cardOpen ? "Hide the detail" : "See how we can tell"}
                                {cardOpen ? <IoIosArrowUp/> : <IoIosArrowDown/>}
                              </button>
                            )}
                            {hasMore && cardOpen && (
                              <div className="seehow-body">
                                {d.evidence && (
                                  <div className="evid">
                                    <span className="evid-h">How we can tell</span>
                                    {d.evidence}
                                  </div>
                                )}
                                {d.second && d.second.length > 0 && (
                                  <div className="secondline">
                                    <span className="sl-h">Second line of focus</span>
                                    {d.second.map((t) => TASK_NAME[t]).join("  +  ")} — practise alongside your main
                                    tasks, just not before them.
                                  </div>
                                )}
                                {d.budget && (
                                  <div className="whyflag" dangerouslySetInnerHTML={{ __html: `<b>The maths check:</b> ${d.budget}` }} />
                                )}
                                {d.brush && d.brush.length > 0 && (
                                  <div className="depri">
                                    {d.brush.map((t) => TASK_NAME[t]).join("  +  ")} — a quick brush-up in the days
                                    before your test is plenty.
                                  </div>
                                )}
                              </div>
                            )}
                            {gap && lead && (
                              <div className="acts">
                                <a className="yt" href={ytLink(TASK_NAME[lead])} target="_blank" rel="noopener noreferrer">
                                  ▶ Watch Varun · {TASK_NAME[lead]}
                                </a>
                                <span className="trial">◷ Covered in your free trial class</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Daily study plan */}
                    {pri.length > 0 && (
                      <div className="sec">
                        <div className="sec-lab">
                          <span className="nm">4</span> Your daily study plan
                        </div>
                        <p style={{ fontSize: "13.5px", color: "var(--pmut)", margin: "-2px 0 13px" }}>
                          Built from Language Academy's practice planner, weighted to your weak spots.
                        </p>
                        <div className="plan">
                          <div className="plan-h">Every day · in priority order</div>
                          {pri.map((p) => (
                            <div className="plan-row" key={p.t}>
                              <span>
                                <b>{TASK_NAME[p.t]}</b>
                              </span>
                              <span className="plan-rep">{REPS[p.t] || "daily"}</span>
                            </div>
                          ))}
                        </div>
                        {brushTasks.length > 0 && (
                          <p className="plan-foot">
                            <b>Only in the last few days before your test</b> (low-value):{" "}
                            {brushTasks.map((t) => TASK_NAME[t]).join("  +  ")} — about 5 each, just to stay
                            familiar.
                          </p>
                        )}
                        <p className="plan-foot">
                          Keep your strong skills sharp with light, occasional practice — don't over-train what's
                          already working.
                        </p>
                      </div>
                    )}

                    {/* Bottom line */}
                    <div className="sec">
                      <div className="sec-lab">
                        <span className="nm">{pri.length > 0 ? 5 : 4}</span> The bottom line
                      </div>
                      <div className="rootcause">
                        <p dangerouslySetInnerHTML={{ __html: report.overall || report.summary || "" }} />
                      </div>
                    </div>

                    <div className="aidisc">
                      <b>A quick note:</b> this is an AI-assisted estimate built from your scores and Skills Profile
                      — a smart guide, not a guarantee, and it can occasionally get something wrong. For an exact,
                      personalised plan, check it with a Language Academy coach.
                    </div>
                  </div>

                  <div className="ctaband">
                    <div>
                      <div className="ct">Want a coach to turn this into a 2-week plan?</div>
                      <div className="cs">Free Trial Class · we’ll bring this exact diagnosis. No obligation.</div>
                    </div>
                    <ButtonComp btnText="Book my free trial" link={() => { const modal = new window.bootstrap.Modal(document.getElementById('staticBackdrop')); modal.show(); }} />
                  </div>
                </div>
                <div className="rep-reset">
                  <button type="button" className="btn btn-ghost" onClick={confirmStartEntry}>
                    Analyse another scorecard
                  </button>
                  <p className="m-0">AI-assisted · built on Pearson's official PTE scoring model · no score prediction</p>
                </div>
              </div>
            )}
          </section>
        </main>

        {/* Goal / validation modal */}
        {modal && (
          <div className="modal-ov show" role="dialog" aria-modal="true">
            <div className="modal-card">
              <div className="modal-t">{modal.title}</div>
              <div className="modal-b">{modal.body}</div>
              <div className="modal-acts">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const cb = modal.onYes;
                    setModal(null);
                    if (cb) cb();
                  }}
                >
                  {modal.yes || "Continue"}
                </button>
                {modal.no && (
                  <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>
                    {modal.no}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {authOpen && (
          <ScorecardAuthModal
            onClose={() => setAuthOpen(false)}
            onAuthenticated={handleAuthenticated}
            initialMode="register"
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ScorecardAnalyzer;